import {
  AppSettings,
  Category,
  CryptoRates,
  DEFAULT_CURRENCY,
  DEFAULT_USDT_PRICE_BDT,
  DEFAULT_WALLETS,
  Transaction,
} from 'types/finance';

export type FinanceState = {
  transactions: Transaction[];
  categories: Category[];
  settings: AppSettings;
};

const API_BASE = process.env.REACT_APP_API_URL || '';

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    headers: { 'Content-Type': 'application/json', ...(init?.headers || {}) },
    ...init,
  });
  if (!res.ok) {
    throw new Error(`API ${res.status}: ${path}`);
  }
  return res.json() as Promise<T>;
}

function normalizeSettings(raw?: Partial<AppSettings> | null): AppSettings {
  return {
    currency: raw?.currency || DEFAULT_CURRENCY,
    usdtPriceBdt:
      raw?.usdtPriceBdt != null ? Number(raw.usdtPriceBdt) : DEFAULT_USDT_PRICE_BDT,
    wallets: Array.isArray(raw?.wallets) && raw!.wallets!.length
      ? raw!.wallets!
      : DEFAULT_WALLETS,
  };
}

export async function fetchState(): Promise<FinanceState> {
  const data = await request<FinanceState>('/api/state');
  return {
    transactions: data.transactions || [],
    categories: data.categories || [],
    settings: normalizeSettings(data.settings),
  };
}

export async function saveState(state: FinanceState): Promise<FinanceState> {
  return request<FinanceState>('/api/state', {
    method: 'PUT',
    body: JSON.stringify(state),
  });
}

export async function saveTransactions(
  transactions: Transaction[]
): Promise<Transaction[]> {
  return request<Transaction[]>('/api/transactions', {
    method: 'PUT',
    body: JSON.stringify(transactions),
  });
}

export async function saveCategories(categories: Category[]): Promise<Category[]> {
  return request<Category[]>('/api/categories', {
    method: 'PUT',
    body: JSON.stringify(categories),
  });
}

export async function saveSettings(
  settings: Partial<AppSettings>
): Promise<AppSettings> {
  const data = await request<AppSettings>('/api/settings', {
    method: 'PUT',
    body: JSON.stringify(settings),
  });
  return normalizeSettings(data);
}

export async function fetchCryptoRates(): Promise<CryptoRates> {
  const data = await request<{ rates: CryptoRates }>('/api/rates');
  return data.rates;
}

export type WalletTransferRow = {
  date: string;
  type: 'income' | 'expense';
  amount: number;
  amountUsdt?: number;
  tokenSymbol: string;
  tokenAmount: number;
  description: string;
  reference: string;
  wallet: string;
  chain?: string;
  walletId?: string;
  walletLabel?: string;
  source?: string;
};

export async function fetchWalletTransfers(): Promise<{
  rates: CryptoRates;
  usdtPriceBdt: number;
  rows: WalletTransferRow[];
  warnings: string[];
}> {
  return request('/api/wallets/transfers');
}
